import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  FormControl,
  FormLabel,
  Input,
  Button,
  Stack,
  Typography,
} from '@mui/joy';
import { FloppyDisk as SaveIcon } from '@phosphor-icons/react';
import type { TopPage } from '../types';

interface TopPageModalProps {
  open: boolean;
  onClose: () => void;
  topPage?: TopPage | null;
  onSave: (data: Omit<TopPage, 'id'>) => void;
  loading?: boolean;
}

export const TopPageModal: React.FC<TopPageModalProps> = ({
  open,
  onClose,
  topPage,
  onSave,
  loading = false,
}) => {
  const [formData, setFormData] = useState<Omit<TopPage, 'id'>>({
    url: '',
    keyword_1: '',
    keyword_2: '',
    keyword_3: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when modal opens or topPage changes
  useEffect(() => {
    if (open) {
      if (topPage) {
        setFormData({
          url: topPage.url || '',
          keyword_1: topPage.keyword_1 || '',
          keyword_2: topPage.keyword_2 || '',
          keyword_3: topPage.keyword_3 || '',
        });
      } else {
        setFormData({
          url: '',
          keyword_1: '',
          keyword_2: '',
          keyword_3: '',
        });
      }
      setErrors({});
    }
  }, [open, topPage]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Keyword 1 is required
    if (!formData.keyword_1.trim()) {
      newErrors.keyword_1 = 'Primary keyword is required';
    }

    // Validate URL if provided
    if (formData.url && formData.url.trim()) {
      try {
        new URL(formData.url.trim());
      } catch {
        // If it doesn't have a protocol, try adding https://
        try {
          new URL(`https://${formData.url.trim()}`);
        } catch {
          newErrors.url = 'Please enter a valid URL';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Trim all values and ensure URL has protocol if provided
      let processedUrl = formData.url?.trim() || '';
      if (processedUrl && !processedUrl.match(/^https?:\/\//)) {
        processedUrl = `https://${processedUrl}`;
      }

      onSave({
        url: processedUrl,
        keyword_1: formData.keyword_1.trim(),
        keyword_2: formData.keyword_2?.trim() || undefined,
        keyword_3: formData.keyword_3?.trim() || undefined,
      });
    }
  };

  const handleInputChange = (field: keyof Omit<TopPage, 'id'>, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ minWidth: 400, maxWidth: 500 }}>
        <ModalClose />
        <DialogTitle>
          {topPage ? 'Edit Top Page' : 'Add Top Page'}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Track important pages on your website for SEO optimization. The primary keyword is required.
              </Typography>

              <FormControl error={!!errors.url}>
                <FormLabel>Page URL</FormLabel>
                <Input
                  type="text"
                  value={formData.url || ''}
                  onChange={(e) => handleInputChange('url', e.target.value)}
                  placeholder="https://example.com/services"
                  slotProps={{
                    input: {
                      'aria-describedby': errors.url ? 'url-error' : undefined,
                    },
                  }}
                />
                {errors.url && (
                  <Typography id="url-error" level="body-sm" color="danger">
                    {errors.url}
                  </Typography>
                )}
              </FormControl>

              <FormControl required error={!!errors.keyword_1}>
                <FormLabel>Primary Keyword</FormLabel>
                <Input
                  value={formData.keyword_1}
                  onChange={(e) => handleInputChange('keyword_1', e.target.value)}
                  placeholder="e.g., roofing services"
                  autoFocus
                  slotProps={{
                    input: {
                      'aria-describedby': errors.keyword_1 ? 'keyword-1-error' : undefined,
                    },
                  }}
                />
                {errors.keyword_1 && (
                  <Typography id="keyword-1-error" level="body-sm" color="danger">
                    {errors.keyword_1}
                  </Typography>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Secondary Keyword</FormLabel>
                <Input
                  value={formData.keyword_2 || ''}
                  onChange={(e) => handleInputChange('keyword_2', e.target.value)}
                  placeholder="e.g., roof repair"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Tertiary Keyword</FormLabel>
                <Input
                  value={formData.keyword_3 || ''}
                  onChange={(e) => handleInputChange('keyword_3', e.target.value)}
                  placeholder="e.g., emergency roofing"
                />
              </FormControl>

              <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: 'flex-end' }}>
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  color="primary"
                  startDecorator={<SaveIcon />}
                  loading={loading}
                >
                  {topPage ? 'Update' : 'Add'} Page
                </Button>
              </Stack>
            </Stack>
          </form>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};